import {
  FILM_SIZES, MAX_INKS, MESH_PRESETS, meshToLpi, nameForColor, newInkId, parseColor,
  type FilmSettings, type FilmSize, type Ink, type ScreenSettings, type SepMode,
} from '@eip/engine';
import type { PlateMeta } from '../worker/protocol';

/** Labels and hints for the mode select. Object key order is `Object.keys` order,
 *  which the UI's select and the test both rely on: sim, then index, then spot. */
export const MODE_LABELS: Record<SepMode, { label: string; hint: string }> = {
  sim: { label: 'Simulated process', hint: 'Photographic art, halftoned inks, white base' },
  index: { label: 'Index', hint: 'Dithered square dots, no moiré' },
  spot: { label: 'Spot colour', hint: 'Flat colours, solid plates' },
};

/** Mesh-preset options for the screening select, e.g. "230 mesh → 46 lpi". */
export function meshOptions(): Array<{ value: string; label: string }> {
  return MESH_PRESETS.map((p) => ({ value: String(p.mesh), label: `${p.mesh} mesh → ${p.lpi} lpi` }));
}

const FILM_SIZE_LABELS: Record<keyof typeof FILM_SIZES, string> = {
  '13x19': '13 × 19 in',
  '17x22': '17 × 22 in',
  a3: 'A3 (11.7 × 16.5 in)',
  a3plus: 'A3+ (12.95 × 19.02 in)',
};

/** What the film-size select can be set to: fit-to-art, a named preset's key, or custom. */
export type FilmSizeChoice = 'fit' | keyof typeof FILM_SIZES | 'custom';

/** Film-size select options: fit-to-art, every named preset, then custom. */
export function filmSizeOptions(): Array<{ value: FilmSizeChoice; label: string }> {
  const presets = (Object.keys(FILM_SIZES) as (keyof typeof FILM_SIZES)[]).map((key) => ({ value: key, label: FILM_SIZE_LABELS[key] }));
  return [{ value: 'fit' as const, label: 'Fit to art' }, ...presets, { value: 'custom' as const, label: 'Custom' }];
}

/** Which select option a `FilmSettings['size']` corresponds to: `'fit'`, the
 *  matching preset key, or `'custom'` for any other explicit size. */
export function filmSizeValue(size: FilmSettings['size']): string {
  if (size === 'fit') return 'fit';
  const key = (Object.keys(FILM_SIZES) as (keyof typeof FILM_SIZES)[]).find(
    (k) => FILM_SIZES[k].widthIn === size.widthIn && FILM_SIZES[k].heightIn === size.heightIn,
  );
  return key ?? 'custom';
}

/** Sets `mesh` and the derived `lpi` (`detail` selects the finer ÷4 preset LPI). */
export function applyMeshPreset(screen: ScreenSettings, mesh: number, detail: boolean): ScreenSettings {
  return { ...screen, mesh, lpi: meshToLpi(mesh, detail) };
}

/**
 * Flips the mesh preset's "detail" (÷4 vs ÷5) toggle to `next`. Re-derives
 * `lpi` from the mesh only when the CURRENT `lpi` still matches what the
 * toggle's other setting would have produced (`meshToLpi(mesh, !next)`) —
 * i.e. the user hasn't hand-typed a custom LPI since the preset was applied.
 * Otherwise the hand-typed value is left alone; only the flag's effect
 * (which the caller tracks) changes.
 */
export function toggleDetail(screen: ScreenSettings, next: boolean): ScreenSettings {
  const matchesPreset = screen.lpi === meshToLpi(screen.mesh, !next);
  return matchesPreset ? { ...screen, lpi: meshToLpi(screen.mesh, next) } : screen;
}

/**
 * The `FilmSettings['size']` to store for a size-select choice. Picking
 * 'custom' keeps whatever size is already set (seeding a non-preset default
 * only when coming from `'fit'`, which has no numbers of its own) rather than
 * resetting it — the stored value alone can't always tell a deliberate custom
 * size apart from one that happens to equal a preset (e.g. "13×19 → Custom"
 * has to keep {13, 19}), so the caller tracks that choice separately in local
 * state rather than relying on `filmSizeValue`.
 */
export function nextFilmSize(current: FilmSettings['size'], choice: FilmSizeChoice): FilmSize | 'fit' {
  if (choice === 'fit') return 'fit';
  if (choice === 'custom') return current === 'fit' ? { widthIn: 14, heightIn: 20 } : current;
  return FILM_SIZES[choice];
}

/**
 * Moves the ink at `id` one place towards its neighbour in press order
 * (`dir` -1 = earlier, +1 = later), swapping array position and renumbering
 * every ink's `order` to its new index. A no-op (returns `inks` unchanged)
 * when the ink is already at that end.
 */
export function reorderInks(inks: Ink[], id: string, dir: -1 | 1): Ink[] {
  const sorted = [...inks].sort((a, b) => a.order - b.order);
  const i = sorted.findIndex((ink) => ink.id === id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sorted.length) return inks;
  [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
  return sorted.map((ink, k) => ({ ...ink, order: k }));
}

/** Patches one ink by id, leaving every other ink (and array order) untouched. */
export function withInkPatch(inks: Ink[], id: string, patch: Partial<Ink>): Ink[] {
  return inks.map((ink) => (ink.id === id ? { ...ink, ...patch } : ink));
}

/** Appends a new ink from a hex colour, named from its colour word, at the
 *  end of press order. Throws once `MAX_INKS` is reached. */
export function addInkFromHex(inks: Ink[], hex: string): Ink[] {
  if (inks.length >= MAX_INKS) throw new Error(`At most ${MAX_INKS} inks are allowed`);
  const rgb = parseColor(hex);
  const next: Ink = { id: newInkId('ui'), name: nameForColor(rgb), rgb, enabled: true, order: inks.length, tolerance: 0.25, softness: 0.12, curve: [1, 1, 1] };
  return [...inks, next];
}

/**
 * Spec §7's "duplicate": a copy of the ink at `id` with a fresh id (the same
 * `newInkId('ui')` scheme `addInkFromHex` uses, so it can never collide with a
 * worker-detected `w-*` id), named `"<name> copy"`, inserted directly AFTER its
 * source in press order with every `order` renumbered. Throws once `MAX_INKS` is
 * reached (the button is disabled at the cap too); returns `inks` unchanged when
 * `id` is not in the list.
 */
export function duplicateInk(inks: Ink[], id: string): Ink[] {
  if (inks.length >= MAX_INKS) throw new Error(`At most ${MAX_INKS} inks are allowed`);
  const sorted = [...inks].sort((a, b) => a.order - b.order);
  const i = sorted.findIndex((ink) => ink.id === id);
  if (i < 0) return inks;
  const src = sorted[i];
  // `curve` is a tuple: copied, not shared, or editing one copy's curve would move the other's.
  const copy: Ink = { ...src, id: newInkId('ui'), name: `${src.name} copy`, curve: [...src.curve] as [number, number, number] };
  return [...sorted.slice(0, i + 1), copy, ...sorted.slice(i + 1)].map((ink, k) => ({ ...ink, order: k }));
}

/** One decimal place, "—" when there is no plate (not yet built). */
export function coverageLabel(meta: PlateMeta | undefined): string {
  return meta ? `${(meta.coverage * 100).toFixed(1)} %` : '—';
}
